import { call, put, select, takeLatest } from 'redux-saga/effects';
import { FetchResult } from 'apollo-boost';
import i18n from 'i18next';

import {
	ExchangePriceParams,
	GetExchangePriceDocument,
	GetExchangePriceQuery,
	GetExchangePriceQueryVariables,
	GetExchangeVariantsDocument,
	GetExchangeVariantsMutation,
	GetExchangeVariantsMutationVariables,
	GetExchangeVariantsParams
} from '@websky/graphql';
import { createClient } from '../../../../../graphql-client';

import {
	FETCH_EXCHANGE_VARIANTS,
	FETCH_EXCHANGE_PRICE,
	clearExchangeVariants,
	setExchangeVariants,
	setExchangeVariantPrices,
	setActualSelectedFlightPriceId
} from './actions';
import { setLoading, stopLoading } from '../isLoading/actions';
import { getExchangePriceParameters, getExchangeVariantsParams } from '../selectors';
import { setError } from '../error/actions';

import { getGqlErrorMessage } from '@shared/lib/getGqlErrorMessage';

function getExchangeVariantsQuery(params: GetExchangeVariantsParams) {
	const client = createClient();

	return client.mutate<GetExchangeVariantsMutation, GetExchangeVariantsMutationVariables>({
		mutation: GetExchangeVariantsDocument,
		variables: { params },
		fetchPolicy: 'no-cache'
	});
}

function getExchangePrice(params: ExchangePriceParams) {
	const client = createClient();

	return client.mutate<GetExchangePriceQuery, GetExchangePriceQueryVariables>({
		mutation: GetExchangePriceDocument,
		variables: { params },
		fetchPolicy: 'no-cache'
	});
}

function* fetchExchangeVariants() {
	const exchangeVariantsParams: GetExchangeVariantsParams = yield select(getExchangeVariantsParams);

	yield put(clearExchangeVariants());
	yield put(setLoading('exchange-variants'));

	try {
		const exchangeVariants: FetchResult<GetExchangeVariantsMutation> = yield call(
			getExchangeVariantsQuery,
			exchangeVariantsParams
		);

		if (!exchangeVariants?.data?.GetExchangeVariants || exchangeVariants.errors?.length) {
			throw new Error(exchangeVariants.errors?.[0]?.message);
		}

		yield put(
			setExchangeVariants({
				flightDirections: exchangeVariants.data.GetExchangeVariants.flightDirections,
				exchangeVariantPriceInfo: exchangeVariants.data.GetExchangeVariants?.exchangeVariantPriceInfo || []
			})
		);
	} catch (err) {
		const errorMessage = i18n.t(getGqlErrorMessage(err), { ns: 'Exchange' });

		yield put(setError(errorMessage));
	}

	yield put(stopLoading());
}

export function* fetchExchangePrice() {
	const exchangePriceParams: ExchangePriceParams = yield select(getExchangePriceParameters);

	yield put(setLoading('exchange-price'));

	try {
		const exchangePrice: FetchResult<GetExchangePriceQuery> = yield call(getExchangePrice, exchangePriceParams);

		if (!exchangePrice.data?.ExchangePrice || exchangePrice.errors?.length) {
			throw new Error(exchangePrice.errors?.[0]?.message);
		}

		// [WEBSKY-8382, WEBSKY-8386]: В ответе может быть другой id рейса — берём первый из списка цен,
		// иначе оставляем id, который отправили в ExchangePrice.
		const exchangeVariantPriceInfo = exchangePrice.data.ExchangePrice.exchangeVariantPriceInfo || [];
		const actualPriceId = exchangeVariantPriceInfo[0]?.flightId || exchangePriceParams.flightId;

		// [WEBSKY-8382, WEBSKY-8386]: Сохраняем оба id: исходный выбор пользователя и актуальный id из ответа для CreateExchange.
		yield put(
			setActualSelectedFlightPriceId({
				priceId: exchangePriceParams.flightId,
				actualPriceId
			})
		);

		yield put(setExchangeVariantPrices(exchangePrice.data.ExchangePrice.exchangeVariantPriceInfo));
	} catch (err) {
		const errorMessage = i18n.t(getGqlErrorMessage(err), { ns: 'Exchange' });

		yield put(setError(errorMessage));
	}

	yield put(stopLoading());
}

export function* exchangeVariantsSagas() {
	yield takeLatest(FETCH_EXCHANGE_VARIANTS, fetchExchangeVariants);
	yield takeLatest(FETCH_EXCHANGE_PRICE, fetchExchangePrice);
}
